import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  IconButton,
  Divider,
  LinearProgress,
  Alert,
  TextField,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
  MenuItem,
  Stack,
} from '@mui/material';
import {
  ArrowBack,
  Save,
  Phone,
  WhatsApp,
  Email,
  LocationOn,
  AccountBalance,
  Person,
  CreditCard,
  Business,
  Work,
  CalendarToday,
  Assessment,
  ExpandMore,
} from '@mui/icons-material';
import { getBorrowerById, updateBorrower } from '../../services/request/Borrowers';
import { BorrowerAPIItem } from '../../models/LendingModels';
import CustomerPicker, { PickedCustomer } from '../../shared-components/customer-picker/CustomerPicker';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const EditCustomerForm: React.FC = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [referrer, setReferrer] = useState<PickedCustomer | null>(null);
  const [formData, setFormData] = useState<Partial<BorrowerAPIItem>>({
    name: '',
    comments: '',
    contactNumber: '',
    whatsappNumber: '',
    isContactSameAsWhatsapp: false,
    email: '',
    address: '',
    pincode: '',
    city: '',
    state: '',
    referralCode: '',
    creditScore: undefined,
    dateOfBirth: '',
    panNumber: '',
    aadhaarNumber: '',
    employmentStatus: '',
    monthlyIncome: undefined,
    occupation: '',
    companyName: '',
    bankAccountNumber: '',
    ifscCode: '',
    bankName: '',
    kycStatus: '',
    riskCategory: '',
    customerSegment: '',
    lastContactDate: '',
    nextFollowupDate: '',
    sourceOfLead: '',
    tags: '',
  });

  useEffect(() => {
    if (customerId) {
      fetchBorrowerData();
    }
  }, [customerId]);

  const fetchBorrowerData = async () => {
    if (!customerId) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await getBorrowerById(customerId);
      
      if (response) {
        // Format dates for input fields
        const formatDateForInput = (dateString?: string) => {
          if (!dateString) return '';
          try {
            const date = new Date(dateString);
            return date.toISOString().split('T')[0];
          } catch {
            return dateString;
          }
        };

        setFormData({
          name: response.name || '',
          comments: response.comments || '',
          contactNumber: response.contactNumber || '',
          whatsappNumber: response.whatsappNumber || '',
          isContactSameAsWhatsapp: response.isContactSameAsWhatsapp || false,
          email: response.email || '',
          address: response.address || '',
          pincode: response.pincode || '',
          city: response.city || '',
          state: response.state || '',
          referralCode: response.referralCode || '',
          creditScore: response.creditScore,
          dateOfBirth: formatDateForInput(response.dateOfBirth),
          panNumber: response.panNumber || '',
          aadhaarNumber: response.aadhaarNumber || '',
          employmentStatus: response.employmentStatus || '',
          monthlyIncome: response.monthlyIncome,
          occupation: response.occupation || '',
          companyName: response.companyName || '',
          bankAccountNumber: response.bankAccountNumber || '',
          ifscCode: response.ifscCode || '',
          bankName: response.bankName || '',
          kycStatus: response.kycStatus || '',
          riskCategory: response.riskCategory || '',
          customerSegment: response.customerSegment || '',
          lastContactDate: formatDateForInput(response.lastContactDate),
          nextFollowupDate: formatDateForInput(response.nextFollowupDate),
          sourceOfLead: response.sourceOfLead || '',
          tags: response.tags || '',
        });
        setReferrer(
          response.referredByBorrowerId
            ? { id: response.referredByBorrowerId, name: response.referredByName || 'Referring customer' }
            : null
        );
      } else {
        setError('Borrower not found');
      }
    } catch (err: any) {
      setError(err?.response?.status === 404 
        ? 'Borrower not found' 
        : `Failed to fetch borrower: ${err.message || 'Unknown error'}`);
      console.error('Error fetching borrower:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };
      
      // Auto-fill WhatsApp number if checkbox is checked
      if (name === 'isContactSameAsWhatsapp' && checked) {
        newData.whatsappNumber = prev.contactNumber || '';
      }
      
      return newData;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name?.trim()) {
      toast.error('Name is required');
      return;
    }

    if (!customerId) return;

    try {
      setSaving(true);
      setError(null);

      // Prepare data for API (convert empty strings to null/undefined for optional fields)
      const updateData: any = {
        name: formData.name,
        comments: formData.comments || null,
        contactNumber: formData.contactNumber || null,
        whatsappNumber: formData.whatsappNumber || null,
        isContactSameAsWhatsapp: formData.isContactSameAsWhatsapp || false,
        email: formData.email || null,
        address: formData.address || null,
        pincode: formData.pincode || null,
        city: formData.city || null,
        state: formData.state || null,
        referralCode: formData.referralCode || null,
        creditScore: formData.creditScore || null,
        dateOfBirth: formData.dateOfBirth || null,
        panNumber: formData.panNumber || null,
        aadhaarNumber: formData.aadhaarNumber || null,
        employmentStatus: formData.employmentStatus || null,
        monthlyIncome: formData.monthlyIncome || null,
        occupation: formData.occupation || null,
        companyName: formData.companyName || null,
        bankAccountNumber: formData.bankAccountNumber || null,
        ifscCode: formData.ifscCode || null,
        bankName: formData.bankName || null,
        kycStatus: formData.kycStatus || null,
        riskCategory: formData.riskCategory || null,
        customerSegment: formData.customerSegment || null,
        lastContactDate: formData.lastContactDate || null,
        nextFollowupDate: formData.nextFollowupDate || null,
        sourceOfLead: formData.sourceOfLead || null,
        tags: formData.tags || null,
        referredByBorrowerId: referrer?.id ?? null,
      };

      const response = await updateBorrower(customerId, updateData);
      
      if (response && (response.code === 200 || response.status === 200)) {
        toast.success('Borrower updated successfully!');
        setTimeout(() => {
          navigate(`/admin/customers/${customerId}`);
        }, 1000);
      } else {
        toast.error('Failed to update borrower');
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err.message || 'Failed to update borrower';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Error updating borrower:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Loading borrower data...</Typography>
      </Box>
    );
  }

  if (error && !formData.name) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={fetchBorrowerData}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/admin/customers')}
          sx={{ mt: 2 }}
        >
          Back to Customers
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <ToastContainer />
      
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate(`/admin/customers/${customerId}`)}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" fontWeight="bold">
            Edit Borrower
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate(`/admin/customers/${customerId}`)}
        >
          Cancel
        </Button>
      </Box>

      <form onSubmit={handleSubmit}>
        {/* Basic Information */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle1" fontWeight="bold">
              <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
              Basic Information
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Name *"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Comments"
                  name="comments"
                  value={formData.comments}
                  onChange={handleChange}
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Contact Information */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle1" fontWeight="bold">
              <Phone sx={{ mr: 1, verticalAlign: 'middle' }} />
              Contact Information
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Contact Number"
                  name="contactNumber"
                  value={formData.contactNumber}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Phone />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="WhatsApp Number"
                  name="whatsappNumber"
                  value={formData.whatsappNumber}
                  onChange={handleChange}
                  disabled={formData.isContactSameAsWhatsapp}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <WhatsApp />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.isContactSameAsWhatsapp || false}
                      onChange={handleChange}
                      name="isContactSameAsWhatsapp"
                    />
                  }
                  label="Contact Number same as WhatsApp Number"
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Address Information */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle1" fontWeight="bold">
              <LocationOn sx={{ mr: 1, verticalAlign: 'middle' }} />
              Address Information
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Pincode"
                  name="pincode"
                  value={formData.pincode}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="State"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* CRM & Business Fields */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle1" fontWeight="bold">
              <Assessment sx={{ mr: 1, verticalAlign: 'middle' }} />
              CRM & Business Information
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Referral Code"
                  name="referralCode"
                  value={formData.referralCode}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomerPicker
                  label="Referred by"
                  size="medium"
                  value={referrer}
                  onChange={setReferrer}
                  excludeIds={[customerId]}
                  helperText="Recommended this customer. Takes no responsibility for repayment."
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Credit Score"
                  name="creditScore"
                  type="number"
                  value={formData.creditScore || ''}
                  onChange={handleChange}
                  inputProps={{ min: 0, max: 900 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Source of Lead"
                  name="sourceOfLead"
                  value={formData.sourceOfLead}
                  onChange={handleChange}
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="WEBSITE">Website</MenuItem>
                  <MenuItem value="REFERRAL">Referral</MenuItem>
                  <MenuItem value="SOCIAL_MEDIA">Social Media</MenuItem>
                  <MenuItem value="DIRECT">Direct</MenuItem>
                  <MenuItem value="PARTNER">Partner</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Tags (comma-separated)"
                  name="tags"
                  value={formData.tags}
                  onChange={handleChange}
                  placeholder="e.g., VIP, Priority, New Customer"
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Additional Fintech Fields */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle1" fontWeight="bold">
              <AccountBalance sx={{ mr: 1, verticalAlign: 'middle' }} />
              Additional Information
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Date of Birth"
                  name="dateOfBirth"
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="PAN Number"
                  name="panNumber"
                  value={formData.panNumber}
                  onChange={handleChange}
                  inputProps={{ maxLength: 10 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Aadhaar Number"
                  name="aadhaarNumber"
                  value={formData.aadhaarNumber}
                  onChange={handleChange}
                  inputProps={{ maxLength: 12 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Employment Status"
                  name="employmentStatus"
                  value={formData.employmentStatus}
                  onChange={handleChange}
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="EMPLOYED">Employed</MenuItem>
                  <MenuItem value="SELF_EMPLOYED">Self Employed</MenuItem>
                  <MenuItem value="UNEMPLOYED">Unemployed</MenuItem>
                  <MenuItem value="RETIRED">Retired</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Monthly Income"
                  name="monthlyIncome"
                  type="number"
                  value={formData.monthlyIncome || ''}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Occupation"
                  name="occupation"
                  value={formData.occupation}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Company Name"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="KYC Status"
                  name="kycStatus"
                  value={formData.kycStatus}
                  onChange={handleChange}
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="PENDING">Pending</MenuItem>
                  <MenuItem value="VERIFIED">Verified</MenuItem>
                  <MenuItem value="REJECTED">Rejected</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Risk Category"
                  name="riskCategory"
                  value={formData.riskCategory}
                  onChange={handleChange}
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="LOW">Low</MenuItem>
                  <MenuItem value="MEDIUM">Medium</MenuItem>
                  <MenuItem value="HIGH">High</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Customer Segment"
                  name="customerSegment"
                  value={formData.customerSegment}
                  onChange={handleChange}
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="PREMIUM">Premium</MenuItem>
                  <MenuItem value="STANDARD">Standard</MenuItem>
                  <MenuItem value="BASIC">Basic</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Bank Account Number"
                  name="bankAccountNumber"
                  value={formData.bankAccountNumber}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="IFSC Code"
                  name="ifscCode"
                  value={formData.ifscCode}
                  onChange={handleChange}
                  inputProps={{ maxLength: 11 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Bank Name"
                  name="bankName"
                  value={formData.bankName}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Last Contact Date"
                  name="lastContactDate"
                  type="date"
                  value={formData.lastContactDate}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Next Follow-up Date"
                  name="nextFollowupDate"
                  type="date"
                  value={formData.nextFollowupDate}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {/* Action Buttons */}
        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={() => navigate(`/admin/customers/${customerId}`)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            type="submit"
            startIcon={<Save />}
            disabled={saving || !formData.name?.trim()}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default EditCustomerForm;
